import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { BadgeCheck, ShieldCheck, Upload, FileText, Building2, UserX, CheckCircle2, Clock, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useI18n } from '@/context/I18nContext';

const STATUS_COLOR = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-100 text-rose-800' };

const REGIME_OPTIONS = [
    { value: 'fisica', label: 'Persona Física (con RFC)', icon: ShieldCheck, color: 'text-emerald-600', desc: 'ISR 2.5% + IVA 8% retenidos' },
    { value: 'moral', label: 'Persona Moral / Empresa (con RFC)', icon: Building2, color: 'text-blue-600', desc: 'ISR 2.5% + IVA 8% retenidos' },
    { value: 'sin_rfc', label: 'Sin RFC registrado', icon: UserX, color: 'text-red-600', desc: 'ISR 20% + IVA 16% retenidos (penalización máxima)' },
];

async function uploadFile(file) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', 'image');
    const { data } = await api.post('/upload', fd);
    return data.url;
}

export default function KycPage() {
    const { t } = useI18n();
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        full_legal_name: '', document_type: 'ine', document_number: '',
        country: 'MX', document_front_url: '', document_back_url: '', selfie_url: '',
        address: '', tax_id: '',
    });

    // Fiscal profile state
    const [fiscal, setFiscal] = useState(null);
    const [fiscalBusy, setFiscalBusy] = useState(false);
    const [fiscalForm, setFiscalForm] = useState({
        fiscal_regime: 'sin_rfc', tax_id: '', fiscal_curp: '',
        fiscal_csf_url: '', fiscal_bank_statement_url: '',
    });

    const load = async () => {
        const [kycRes, fiscalRes] = await Promise.all([
            api.get('/me/kyc'),
            api.get('/me/fiscal-profile').catch(() => ({ data: null })),
        ]);
        setState(kycRes.data);
        if (kycRes.data?.latest) setForm(f => ({ ...f, ...kycRes.data.latest }));
        if (fiscalRes.data) {
            setFiscal(fiscalRes.data);
            setFiscalForm({
                fiscal_regime: fiscalRes.data.fiscal_regime || 'sin_rfc',
                tax_id: fiscalRes.data.tax_id || '',
                fiscal_curp: fiscalRes.data.fiscal_curp || '',
                fiscal_csf_url: fiscalRes.data.fiscal_csf_url || '',
                fiscal_bank_statement_url: fiscalRes.data.fiscal_bank_statement_url || '',
            });
        }
    };
    useEffect(() => { load(); }, []);

    const onFile = (field) => async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try { const url = await uploadFile(f); setForm(x => ({ ...x, [field]: url })); toast.success('Imagen subida'); } catch (_) { toast.error('No se pudo subir'); }
    };

    const onFiscalFile = (field) => async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try { const url = await uploadFile(f); setFiscalForm(x => ({ ...x, [field]: url })); toast.success('Documento subido'); } catch (_) { toast.error('No se pudo subir'); }
    };

    const submit = async () => {
        for (const k of ['full_legal_name', 'document_number', 'document_front_url', 'selfie_url']) {
            if (!form[k]) { toast.error('Completa el campo: ' + k); return; }
        }
        setBusy(true);
        try {
            await api.post('/kyc/submit', form);
            toast.success('Solicitud enviada. Será revisada por el equipo.');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo enviar');
        } finally { setBusy(false); }
    };

    const saveFiscal = async () => {
        setFiscalBusy(true);
        try {
            await api.put('/me/fiscal-profile', fiscalForm);
            toast.success('Perfil fiscal actualizado');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setFiscalBusy(false); }
    };

    const latest = state?.latest;
    const canResubmit = !latest || latest.status === 'rejected';
    const selectedRegime = REGIME_OPTIONS.find(r => r.value === fiscalForm.fiscal_regime) || REGIME_OPTIONS[2];
    const RegimeIcon = selectedRegime.icon;

    return (
        <div className="space-y-6" data-testid="kyc-page">
            {/* Identity verification card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-red-600" />{t('kyc.title', 'Verificación de vendedor')}</CardTitle>
                    <p className="text-sm text-muted-foreground">{t('kyc.subtitle', 'Sube tus documentos y activa el badge de vendedor verificado.')}</p>
                </CardHeader>
                <CardContent>
                    {latest ? (
                        <div className="mb-4 p-3 rounded-lg border flex items-center gap-3">
                            <Badge className={STATUS_COLOR[latest.status]}>{t(`kyc.status.${latest.status}`, latest.status)}</Badge>
                            {latest.status === 'approved' && <BadgeCheck className="h-5 w-5 text-emerald-600" />}
                            {latest.reviewer_notes && <span className="text-sm text-muted-foreground">Notas: {latest.reviewer_notes}</span>}
                        </div>
                    ) : null}

                    {canResubmit ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label>Nombre legal completo</Label>
                                <Input value={form.full_legal_name} onChange={e => setForm({ ...form, full_legal_name: e.target.value })} data-testid="kyc-full-name" />
                            </div>
                            <div>
                                <Label>Tipo de documento</Label>
                                <Select value={form.document_type} onValueChange={v => setForm({ ...form, document_type: v })}>
                                    <SelectTrigger data-testid="kyc-doc-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ine">INE / IFE</SelectItem>
                                        <SelectItem value="passport">Pasaporte</SelectItem>
                                        <SelectItem value="driver_license">Licencia de conducir</SelectItem>
                                        <SelectItem value="other">Otro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Número de documento</Label>
                                <Input value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })} data-testid="kyc-doc-number" />
                            </div>
                            <div>
                                <Label>País</Label>
                                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="MX" data-testid="kyc-country" />
                            </div>
                            <div>
                                <Label>RFC (opcional)</Label>
                                <Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} data-testid="kyc-tax-id" />
                            </div>
                            <div>
                                <Label>Dirección (opcional)</Label>
                                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="kyc-address" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>Documento (frente)</Label>
                                <div className="flex gap-2 items-center">
                                    <Input type="file" accept="image/*" onChange={onFile('document_front_url')} data-testid="kyc-front-file" />
                                    {form.document_front_url && <a className="text-xs text-red-600 underline" href={form.document_front_url} target="_blank" rel="noreferrer">Ver</a>}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <Label>Documento (reverso, opcional)</Label>
                                <div className="flex gap-2 items-center">
                                    <Input type="file" accept="image/*" onChange={onFile('document_back_url')} data-testid="kyc-back-file" />
                                    {form.document_back_url && <a className="text-xs text-red-600 underline" href={form.document_back_url} target="_blank" rel="noreferrer">Ver</a>}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <Label>Selfie sosteniendo tu documento</Label>
                                <div className="flex gap-2 items-center">
                                    <Input type="file" accept="image/*" onChange={onFile('selfie_url')} data-testid="kyc-selfie-file" />
                                    {form.selfie_url && <a className="text-xs text-red-600 underline" href={form.selfie_url} target="_blank" rel="noreferrer">Ver</a>}
                                </div>
                            </div>
                            <div className="md:col-span-2 flex justify-end pt-2">
                                <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="kyc-submit">
                                    <Upload className="h-4 w-4 mr-1" />{busy ? '…' : t('kyc.submit', 'Enviar solicitud')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 border rounded-lg bg-secondary/30 flex items-center gap-2">
                            <BadgeCheck className="h-5 w-5 text-emerald-600" />
                            <span>Tu solicitud está <strong>{t(`kyc.status.${latest?.status}`, latest?.status)}</strong>. No es necesario reenviar.</span>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Fiscal verification card */}
            <Card id="fiscal" data-testid="fiscal-profile-card">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-red-600" />Verificación Fiscal
                            </CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                                Registra tu información fiscal para que la plataforma aplique las tasas de retención correctas (ISR/IVA) ante el SAT.
                            </p>
                        </div>
                        {fiscal && (
                            <Badge className={fiscal.fiscal_profile_active ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                {fiscal.fiscal_profile_active ? (
                                    <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Activo</>
                                ) : (
                                    <><Clock className="h-3.5 w-3.5 mr-1" />Pendiente de activación</>
                                )}
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-5">
                    {/* Status note */}
                    {fiscal?.fiscal_profile_active ? (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2 text-sm text-emerald-800">
                            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                            <div>
                                <strong>Perfil fiscal activo.</strong> Tus retenciones aplican según el régimen <strong>{selectedRegime.label}</strong>.
                                Las tasas se ajustan automáticamente en cada venta.
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <div>
                                <strong>Perfil fiscal inactivo.</strong> Se aplicará la tasa máxima (ISR 20% + IVA 16%) hasta que el equipo active tu perfil tras verificar tus documentos.
                            </div>
                        </div>
                    )}

                    {/* Regime selector */}
                    <div>
                        <Label className="text-sm font-medium">Régimen fiscal</Label>
                        <p className="text-xs text-muted-foreground mb-2">Selecciona cómo facturas o tributas ante el SAT.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
                            {REGIME_OPTIONS.map(r => {
                                const Icon = r.icon;
                                const selected = fiscalForm.fiscal_regime === r.value;
                                return (
                                    <button key={r.value} type="button" onClick={() => setFiscalForm(f => ({ ...f, fiscal_regime: r.value }))}
                                        className={`text-left p-3 rounded-xl border-2 transition-all ${selected ? 'border-red-500 bg-red-50/50' : 'border-transparent bg-secondary/40 hover:bg-secondary/80'}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <Icon className={`h-4 w-4 ${r.color}`} />
                                            <span className="text-xs font-semibold leading-tight">{r.label}</span>
                                        </div>
                                        <span className="text-[11px] text-muted-foreground">{r.desc}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* RFC */}
                    {fiscalForm.fiscal_regime !== 'sin_rfc' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-sm">RFC</Label>
                                <Input value={fiscalForm.tax_id} onChange={e => setFiscalForm(f => ({ ...f, tax_id: e.target.value.toUpperCase() }))}
                                    placeholder="AABB010203A99" maxLength={13} className="mt-1 font-mono uppercase" data-testid="fiscal-rfc" />
                                <p className="text-[11px] text-muted-foreground mt-1">12 caracteres (persona física) o 13 (moral)</p>
                            </div>
                            <div>
                                <Label className="text-sm">CURP</Label>
                                <Input value={fiscalForm.fiscal_curp} onChange={e => setFiscalForm(f => ({ ...f, fiscal_curp: e.target.value.toUpperCase() }))}
                                    placeholder="AABB010203HMCLNS09" maxLength={18} className="mt-1 font-mono uppercase" data-testid="fiscal-curp" />
                                <p className="text-[11px] text-muted-foreground mt-1">Solo aplica para personas físicas</p>
                            </div>
                        </div>
                    )}

                    {/* Document uploads */}
                    {fiscalForm.fiscal_regime !== 'sin_rfc' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="p-3 rounded-xl border space-y-2">
                                <Label className="text-sm font-medium flex items-center gap-1.5">
                                    <FileText className="h-3.5 w-3.5 text-red-600" />Constancia de Situación Fiscal (CSF)
                                </Label>
                                <p className="text-[11px] text-muted-foreground">Documento emitido por el SAT que acredita tu RFC y régimen fiscal.</p>
                                <div className="flex items-center gap-2">
                                    <Input type="file" accept="image/*,.pdf" onChange={onFiscalFile('fiscal_csf_url')} data-testid="fiscal-csf-file" className="text-xs" />
                                </div>
                                {fiscalForm.fiscal_csf_url && (
                                    <a href={fiscalForm.fiscal_csf_url} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                                        <CheckCircle2 className="h-3 w-3" />Documento subido <ExternalLink className="h-3 w-3" />
                                    </a>
                                )}
                            </div>
                            <div className="p-3 rounded-xl border space-y-2">
                                <Label className="text-sm font-medium flex items-center gap-1.5">
                                    <FileText className="h-3.5 w-3.5 text-blue-600" />Estado de cuenta bancario
                                </Label>
                                <p className="text-[11px] text-muted-foreground">Estado de cuenta reciente donde se reciben tus pagos (últimos 3 meses).</p>
                                <div className="flex items-center gap-2">
                                    <Input type="file" accept="image/*,.pdf" onChange={onFiscalFile('fiscal_bank_statement_url')} data-testid="fiscal-bank-file" className="text-xs" />
                                </div>
                                {fiscalForm.fiscal_bank_statement_url && (
                                    <a href={fiscalForm.fiscal_bank_statement_url} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                                        <CheckCircle2 className="h-3 w-3" />Documento subido <ExternalLink className="h-3 w-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end border-t pt-4">
                        <Button onClick={saveFiscal} disabled={fiscalBusy} className="bg-red-600 hover:bg-red-700" data-testid="fiscal-save">
                            {fiscalBusy ? 'Guardando…' : 'Guardar perfil fiscal'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
